import { AutocompleteProps } from "@mui/material";
import CoreAutocomplete from "../select/Autocomplete";
import { FILE_TYPE_OPTIONS } from "./fileConstants";
import CoreTextField, { CoreTextFieldProps } from "../form/TextField";
import CoreChip, { CoreChipProps } from "../chip";
import CloseIcon from "../images/close-x.svg?react";
import { CoreIconButton } from "../button/Button";
import i18nPackage from "../../i18n/i18n";

export type FileTypeOption = {
	value: string;
	label: string;
};

type CoreFileTypeSelectorProps = {
	options?: FileTypeOption[];
	selectedOptions: FileTypeOption[];
	onChange: (selected: FileTypeOption[]) => void;
	placeholder?: string;
	slotProps?: {
		textField?: Omit<CoreTextFieldProps, "placeholder">;
		chip?: Omit<CoreChipProps, "children">;
	};
} & Omit<
	AutocompleteProps<FileTypeOption, true, true, true>,
	"options" | "value" | "onChange" | "renderInput" | "multiple" | "freeSolo" | "isOptionEqualToValue" | "renderTags"
>;

export default function CoreFileTypeSelector({
	options = FILE_TYPE_OPTIONS,
	selectedOptions,
	onChange,
	placeholder,
	slotProps,
	...props
}: CoreFileTypeSelectorProps) {
	return (
		<CoreAutocomplete<FileTypeOption, true, true, true>
			filterSelectedOptions
			options={options}
			value={selectedOptions}
			onChange={(_, newValue) => {
				const lastItem = newValue[newValue.length - 1];

				if (!lastItem) {
					onChange([]);
					return;
				}

				// User added new item via freeSolo
				if (typeof lastItem === "string") {
					const cleanValue = lastItem.trim().toLowerCase();

					// Check for duplicate
					if (selectedOptions.some((opt) => opt.value === cleanValue)) {
						return;
					}

					// Not a duplicate: Add the new object to the existing array
					onChange([...selectedOptions, { label: lastItem.trim(), value: cleanValue }]);
				} else {
					// Dropdown
					onChange(newValue as FileTypeOption[]);
				}
			}}
			renderInput={(params) => (
				<CoreTextField
					{...params}
					ariaLabel={placeholder}
					placeholder={selectedOptions.length > 0 ? "" : placeholder}
					sx={{
						"&:has(.MuiChip-root:hover)": {
							"& .MuiOutlinedInput-root": {
								backgroundColor: "transparent !important",
							},
						},

						...slotProps?.textField?.sx,
					}}
					{...slotProps?.textField}
				/>
			)}
			isOptionEqualToValue={(option, value) => option.value === value.value}
			renderTags={(value: FileTypeOption[], getTagProps) =>
				value.map((option: FileTypeOption, index: number) => {
					const { key, onDelete, ...other } = getTagProps({ index });
					return (
						<CoreChip
							key={key}
							{...other}
							chipColor="#F9F9F9"
							onDelete={onDelete}
							sx={{
								height: "32px",
								border: "0.42px solid #727272",
								padding: "0.25rem 0.5rem",
								color: "#000000",
								fontSize: "0.75rem",
								"& .MuiChip-deleteIcon": {
									margin: 0, // Reset default MUI margin
								},

								"&:hover:not(:has(.MuiChip-deleteIcon:hover))": {
									backgroundColor: "#F2F2F2",
									fontWeight: 600,
									cursor: "auto",
								},

								"&.Mui-focusVisible": {
									border: "2px solid var(--focus-outline-color-dark)",
									backgroundColor: "#F9F9F9",
								},

								...slotProps?.chip?.sx,
							}}
							deleteIcon={
								<CoreIconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										if (onDelete) onDelete(e);
									}}
									variant="plain"
									sx={{ borderRadius: "2px", "&:focus-visible": { outline: "none" } }}
									aria-label={i18nPackage.t("coreComponents.fileTypeSelector.removeAriaLabel", {
										label: option.label,
									})}
								>
									<CloseIcon style={{ width: "18px", height: "18px", color: "#000000" }} />
								</CoreIconButton>
							}
							{...slotProps?.chip}
						>
							{option.label}
						</CoreChip>
					);
				})
			}
			multiple
			freeSolo
			disableClearable
			size="medium"
			{...props}
		/>
	);
}
